import React, { useContext, ReactNode } from 'react';
import { ToastProvider } from 'common/toast';
import { useRoutes } from 'react-router-dom';
import styled from 'styled-components';

import { useSelector } from 'react-redux';
import TransitionScreen from './common/components/TransitionScreen';
import routes from './routing';
import { Authenticator, useTheme, View, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { signIn, type SignInInput } from 'aws-amplify/auth';
import useFeatures from './common/hooks/useFeatures';
import ScrollToTop from 'common/components/ScrollToTop';
import AuthenticatedApp from './AuthenticatedApp';
import './Authentication.css';
import { DomainContext } from 'common/layouts/LayoutContext';
import MandatoryLinks from './areas/authentication/components/MandatoryLinks';
import { selectIsLoginPending } from './store/selectors/auth';
import { UserContextInitializationState } from './common/slices/userContextSlice';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { postLoginCallbackTestMode } from './routing/utils/LoginUtils';

// Test mode authentication provider that bypasses Cognito/Amplify
const TestModeAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Mock signOut function for test mode
  const mockSignOut = () => {
    localStorage.removeItem('cognito_token');
    localStorage.removeItem('test_jwt_token');
    setIsAuthenticated(false);
  };

  // Call postLoginCallbackTestMode immediately when component mounts
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Skip auto-login if we're on the logout path
        if (location.pathname === '/logout') {
          setIsLoading(false);
          return;
        }

        // Check for JWT token immediately
        const token =
          localStorage.getItem('cognito_token') ||
          localStorage.getItem('jwt_token') ||
          localStorage.getItem('access_token');

        if (token) {
          await postLoginCallbackTestMode(mockSignOut, navigate, dispatch, true);
          setIsAuthenticated(true);
          setHasInitialized(true);
        } else {
          // Wait a bit and check again
          setTimeout(async () => {
            const retryToken =
              localStorage.getItem('cognito_token') ||
              localStorage.getItem('jwt_token') ||
              localStorage.getItem('access_token');
            if (retryToken) {
              await postLoginCallbackTestMode(mockSignOut, navigate, dispatch, true);
            }
            setHasInitialized(true);
            setIsLoading(false);
          }, 2000);
          return;
        }

        setIsLoading(false);
      } catch (error: any) {
        console.error('TestModeAuthProvider: Error in postLoginCallbackTestMode:', error);
        setError('There was a problem setting up your session. Please try again.');
        setIsLoading(false);
      }
    };

    if (!hasInitialized) {
      initializeAuth();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, navigate, hasInitialized]); // Removed location.pathname to prevent infinite renders

  const isTestMode = process.env.REACT_APP_TEST_MODE === 'true';
  // Always wait for authentication to complete before rendering
  // This prevents race conditions where protected routes check auth before it's ready
  if (isLoading && !isTestMode) {
    return <TransitionScreen message="Preparing your nuLogic experience" signOut={mockSignOut} />;
  }

  if (error) {
    return <TransitionScreen message={error} isError={true} signOut={mockSignOut} />;
  }

  // If not authenticated, show a simple login form for test mode
  if (!isAuthenticated) {
    return (
      <AuthContainer>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
          }}
        >
          <h2>Test Mode Login</h2>
          <p>Enter a JWT token to authenticate:</p>
          <input
            type="text"
            placeholder="Enter JWT token"
            style={{
              width: '300px',
              padding: '10px',
              margin: '10px 0',
              border: '1px solid #ccc',
              borderRadius: '4px',
            }}
            onKeyPress={async (e) => {
              if (e.key === 'Enter') {
                const token = e.currentTarget.value;
                if (token) {
                  localStorage.setItem('cognito_token', token);
                  setIsAuthenticated(true);
                  setIsLoading(true);
                  // Re-initialize auth with the new token
                  try {
                    await postLoginCallbackTestMode(mockSignOut, navigate, dispatch, true);
                    setIsLoading(false);
                  } catch (error) {
                    console.error('Error during login:', error);
                    setError('Authentication failed. Please check your token.');
                    setIsLoading(false);
                    setIsAuthenticated(false);
                  }
                }
              }
            }}
          />
          <p style={{ fontSize: '12px', color: '#666' }}>
            In test mode, you can use any valid JWT token for authentication.
          </p>
        </div>
      </AuthContainer>
    );
  }

  // After loading completes and user is authenticated, render children
  return <>{children}</>;
};

export const authenticatorConfig = {
  hideSignUp: true,
};

const components = {
  Header() {
    const { tokens } = useTheme();
    const Domain = useContext(DomainContext);

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <IMG src={Domain.fullImage} style={{ display: 'flex', justifySelf: 'center' }} />
      </View>
    );
  },

  Footer() {
    const { tokens } = useTheme();
    const Domain = useContext(DomainContext);
    return (
      <View textAlign="center" padding={tokens.space.large}>
        <MandatoryLinks Domain={Domain} />
      </View>
    );
  },
};

interface AuthenticatedContentProps {
  children: ReactNode;
}

const AuthenticatedContent = ({ children }: AuthenticatedContentProps) => {
  const isAuthPending = useSelector(selectIsLoginPending);
  const { signOut } = useAuthenticator();
  const authState = useSelector((state: any) => state.userContext.initializationState);

  if (isAuthPending || authState === UserContextInitializationState.ORGANIZATION_TRANSITIONING) {
    return (
      <TransitionScreen
        message={
          authState === UserContextInitializationState.ORGANIZATION_TRANSITIONING
            ? 'Switching organization...'
            : 'Preparing your nuLogic experience'
        }
        signOut={signOut}
      />
    );
  }

  return children;
};

const ApplicationRouting = () => {
  const routing = useRoutes(routes);
  const { initialized } = useFeatures();
  const location = useLocation();

  if (!initialized) {
    return <></>;
  }

  // Public paths that should bypass the Authenticator
  const publicPaths = [
    '/auth/setup-password',
    '/account/setup-password',
    '/account/register-token',
    '/account/privacy-policy',
    '/account/terms',
    '/content',
  ];
  
  const isPublicPath = publicPaths.some(path => location.pathname.startsWith(path));

  const appContent = (
    <ToastProvider autoDismiss
      autoDismissTimeout={1500}
      transitionDuration={300}  
    >
      <ScrollToTop />
      {routing}
    </ToastProvider>
  );

  const isTestMode = process.env.REACT_APP_TEST_MODE === 'true';

  if (isTestMode) {
    return (
      <AuthContainer>
        <TestModeAuthProvider>{appContent}</TestModeAuthProvider>
      </AuthContainer>
    );
  }

  // Render public paths without Authenticator
  if (isPublicPath) {
    return appContent;
  }

  // During a Cognito pool migration, REACT_APP_COGNITO_AUTH_FLOW_TYPE=USER_PASSWORD_AUTH
  // routes the Authenticator's signIn through plaintext-password flow so the
  // UserMigration Lambda can receive event.request.password and migrate the user
  // to the new pool. After soak completes, unset this env var to revert to SRP.
  type CognitoAuthFlowType = 'USER_AUTH' | 'USER_SRP_AUTH' | 'USER_PASSWORD_AUTH' | 'CUSTOM_WITHOUT_SRP' | 'CUSTOM_WITH_SRP';
  const cognitoAuthFlowType = process.env.REACT_APP_COGNITO_AUTH_FLOW_TYPE as CognitoAuthFlowType | undefined;
  const authenticatorServices = cognitoAuthFlowType
    ? {
        async handleSignIn(input: SignInInput) {
          return signIn({
            ...input,
            options: {
              ...input.options,
              authFlowType: cognitoAuthFlowType,
            },
          });
        },
      }
    : undefined;

  return (
    <AuthContainer>
      <Authenticator services={authenticatorServices} hideSignUp={authenticatorConfig.hideSignUp} components={components}>
        <AuthenticatedContent>
          <AuthenticatedApp>{appContent}</AuthenticatedApp>
        </AuthenticatedContent>
      </Authenticator>
    </AuthContainer>
  );
};

export default ApplicationRouting;

const IMG = styled.img`
  width: 160px;
  height: auto;
`;

const AuthContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;
